import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getBackendName,
  getStoredAccountingBackend,
} from "../../../../config/accounting";
import { Tabs } from "../../../../ui/components/Tabs";
import { CustomersMastersTab } from "../../../masters/presentation/components/CustomersMastersTab";
import { VendorsMastersTab } from "../../../masters/presentation/components/VendorsMastersTab";
import { SettingsManagedPanel } from "../components/SettingsManagedPanel";
import { SettingsLayout } from "../layout/SettingsLayout";

type PartyTab = "customers" | "vendors";

const TABS: PartyTab[] = ["customers", "vendors"];

const isPartyTab = (value: string | null): value is PartyTab =>
  value !== null && (TABS as string[]).includes(value);

/**
 * CustomersVendorsPage — `/settings/customers-vendors`.
 *
 * REUSES the masters tabs, the same way the LUT panel reuses
 * `ComplianceMastersTab` and Chart of Accounts reuses `GlsSection`. Both tabs
 * are in production at `/dashboard/masters?tab=customers|vendors` and already
 * carry search, add, edit, delete-in-Zoho, bulk upload and the review-request
 * flow. A second copy would be a second place to fix all of it.
 *
 * TWO TABS RATHER THAN ONE LIST, because they are different books with
 * different questions — only a vendor needs a TDS section, only a customer has
 * a credit limit — and a CA works through one and then the other.
 *
 * Add and Edit leave the settings shell: both tabs push to `/add-customer`,
 * `/edit-party` and friends, which are full-page forms outside `/settings`.
 * They come back with `navigate(-1)`, so the return lands on this panel rather
 * than on the masters screen. Nothing to change for that to work, but it is
 * worth knowing before wondering why the rail disappears mid-flow.
 */
export const CustomersVendorsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // The tab lives in the query string so a link can point at one, and a reload
  // stays where it was. Customers first: they are the side most tenants set up
  // first, and something has to be the default.
  const requested = new URLSearchParams(location.search).get("tab");
  const activeTab: PartyTab = isPartyTab(requested) ? requested : "customers";

  const selectTab = (tab: PartyTab): void => {
    const params = new URLSearchParams(location.search);
    if (tab === "customers") {
      params.delete("tab");
    } else {
      params.set("tab", tab);
    }
    const query = params.toString();
    navigate(
      { pathname: location.pathname, search: query ? `?${query}` : "" },
      { replace: true },
    );
  };

  const backendName = getBackendName(getStoredAccountingBackend());

  return (
    <SettingsLayout wide>
      <SettingsManagedPanel
        title="Customers & vendors"
        description={`The parties you invoice and the ones who bill you. Changes here are written to ${backendName}.`}
      >
        <Tabs>
          <Tabs.Item
            active={activeTab === "customers"}
            onClick={() => selectTab("customers")}
          >
            Customers
          </Tabs.Item>
          <Tabs.Item
            active={activeTab === "vendors"}
            onClick={() => selectTab("vendors")}
          >
            Vendors
          </Tabs.Item>
        </Tabs>

        {activeTab === "customers" ? (
          <CustomersMastersTab />
        ) : (
          <VendorsMastersTab />
        )}
      </SettingsManagedPanel>
    </SettingsLayout>
  );
};
